import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import {
  format,
  addDays,
  startOfWeek,
  parseISO,
  isToday,
  isTomorrow,
  isYesterday,
  endOfWeek,
  startOfMonth,
  endOfMonth,
} from "date-fns";

import { vi } from "date-fns/locale";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getWeekDates(weekStart: Date) {
  const dates = [];
  for (let i = 0; i < 5; i++) {
    dates.push(addDays(weekStart, i));
  }
  return dates;
}

export function formatDate(date: Date): string {
  return format(date, "dd/MM/yyyy");
}

export function formatDateDisplay(date: Date): string {
  return format(date, "EEE, MMM d");
}

export function getCurrentWeekStart(): Date {
  return startOfWeek(new Date(), { weekStartsOn: 1 }); // Monday
}

export function getWeekDayName(date: Date): string {
  return format(date, "EEEE");
}

export function getShortDayName(date: Date): string {
  return format(date, "EEE");
}

export const formatVietnameseDate = (date: Date): string => {
  const days = ["Chủ nhật", "T2", "T3", "T4", "T5", "T6", "T7"];

  const dayName = days[date.getDay()];

  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();

  return `${dayName}, ${dd}/${mm}/${yyyy}`;
};

export function formatDateWithDay(dateString: string): string {
  const date = parseISO(dateString);
  const dayName = format(date, "EEEE", { locale: vi });
  const capitalizedDay = dayName.charAt(0).toUpperCase() + dayName.slice(1);
  return `${capitalizedDay}, ${format(date, "dd/MM/yyyy", { locale: vi })}`;
}

export function formatTime(timeString: string): string {
  return timeString;
}

export function formatTimeRange(startTime: string, endTime: string): string {
  return `${startTime} - ${endTime}`;
}

export function getRelativeDate(dateString: string): string | null {
  const date = parseISO(dateString);
  if (isToday(date)) return "Hôm nay";
  if (isTomorrow(date)) return "Ngày mai";
  if (isYesterday(date)) return "Hôm qua";
  return null;
}

export function getWeekRange(date: Date = new Date()): {
  start: Date;
  end: Date;
} {
  return {
    start: startOfWeek(date, { weekStartsOn: 1 }),
    end: endOfWeek(date, { weekStartsOn: 1 }),
  };
}

export function getMonthRange(date: Date = new Date()): {
  start: Date;
  end: Date;
} {
  return {
    start: startOfMonth(date),
    end: endOfMonth(date),
  };
}

export function getDateString(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

export function generateWeekDays(startDate: Date): string[] {
  return Array.from({ length: 7 }, (_, i) =>
    getDateString(addDays(startDate, i))
  );
}

export function formatMonthYear(date: Date): string {
  const monthName = format(date, "MMMM", { locale: vi });
  const capitalizedMonth =
    monthName.charAt(0).toUpperCase() + monthName.slice(1);
  return `${capitalizedMonth} ${format(date, "yyyy")}`;
}

export function getDayName(dateString: string): string {
  const date = parseISO(dateString);
  const dayName = format(date, "EEE", { locale: vi });
  return dayName.charAt(0).toUpperCase() + dayName.slice(1);
}

export function getDayNumber(dateString: string): string {
  const date = parseISO(dateString);
  return format(date, "d");
}

export function formatDateString(dateString: string): string {
  const date = parseISO(dateString);
  return format(date, "dd/MM/yyyy", { locale: vi });
}

export function formatDateStringDisplay(dateString: string): string {
  const relativeDate = getRelativeDate(dateString);
  const formattedDate = formatDateWithDay(dateString);
  
  if (relativeDate) {
    return `${relativeDate} - ${formattedDate}`;
  }
  return formattedDate;
}